import { RulesEngineForm } from '../rules-engine-form-page-old';
import { Policy, RuleModel } from '../../../../../types';
import RulesDetailsSummaryOld from '../rules-details-summary-old';
import { triggerEventOptions } from '../trigger-event-options';
import { actionOptions } from '../action-options';
import { useMemo } from 'react';
import { documentTypeOptions } from '../../../../shared/google-document-types';
import { useQuery, useQueries } from '@tanstack/react-query';
import {
  UserKeys,
  getAllResourceCalendars,
  getCustomSchemas,
  getDocById,
  getSharedDrivesByIdsQuery,
} from '../../../../api/users-api';
import {
  sizeComparisonOptions,
  sizeTypeOptions,
} from '../../../../shared/file-size-utils';
import {
  employeeInfoFields,
  profileFieldOptions,
} from '../../../../shared/shared-profile-field-filter';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface RulesEngineReviewStepProps {
  selectedRule: RulesEngineForm;
  allPolicies: Policy[] | undefined;
}

/**
 * Transforms the form-specific `RulesEngineForm` into a `RuleModel` compatible with `RulesDetailsSummary`.
 * This primarily involves converting `profileFieldFilter.profileFieldValues` from an array of objects
 * to an array of strings.
 */
function transformFormToModel(form: RulesEngineForm): RuleModel {
  const { profileFieldFilter, ...restOfForm } = form;

  let modelProfileFieldFilter: RuleModel['profileFieldFilter'];

  if (profileFieldFilter) {
    const { profileFieldValues, ...restOfProfileFilter } = profileFieldFilter;

    // Convert array of objects to array of strings
    const valuesAsStrings =
      profileFieldValues
        ?.map((v: any) => (typeof v === 'string' ? v : v?.value))
        .filter((v): v is string => v !== null && v !== undefined) ?? [];

    modelProfileFieldFilter = {
      ...restOfProfileFilter,
      profileFieldValues: valuesAsStrings,
    };
  }

  return {
    ...restOfForm,
    profileFieldFilter: modelProfileFieldFilter,
  } as RuleModel;
}

export default function RulesEngineReviewStep({
  selectedRule,
  allPolicies,
}: RulesEngineReviewStepProps) {
  // The selectedRule is in the form's format. We need to transform it
  // to the data model's format before passing it to the summary component.
  const ruleAsModel = transformFormToModel(selectedRule);

  const { data: resourceCalendars } = useQuery({
    queryKey: [UserKeys.ResourceCalendars],
    queryFn: () => getAllResourceCalendars(),
  });

  const { data: customSchemas } = useQuery({
    queryKey: ['customSchemas'],
    queryFn: getCustomSchemas,
  });

  const calendarEmailToNameMap = useMemo(() => {
    if (!resourceCalendars) {
      return new Map<string, string>();
    }
    return new Map(
      resourceCalendars.map((c) => [c.resourceEmail, c.resourceName])
    );
  }, [resourceCalendars]);

  // Function to resolve profile field display names
  const resolveProfileFieldDisplay = (profileFieldType: string): string => {
    const empField = employeeInfoFields.find(
      (f) => f.value === profileFieldType
    );
    if (empField) return empField.display;

    // Check primary organization fields
    const primaryOrgField = profileFieldOptions.find(
      (f) => f.value === profileFieldType
    );
    if (primaryOrgField) return primaryOrgField.display;

    if (profileFieldType.startsWith('customAttribute.')) {
      if (customSchemas) {
        for (const schema of customSchemas) {
          const field = schema.fields?.find(
            (f) =>
              `customAttribute.${schema.schemaName}.${f.fieldName}` ===
              profileFieldType
          );
          if (field) {
            return `${schema.displayName} - ${field.displayName}`;
          }
        }
      }

      // Fallback if schema not found yet
      const parts = profileFieldType.split('.');
      if (parts.length >= 3) {
        const category = parts[1];
        const fieldName = parts.slice(2).join('.');
        return `${category} - ${fieldName}`;
      }
      return profileFieldType;
    }
    return profileFieldType;
  };

  const { triggerInfo, actionInfo, profileFieldFilter } = selectedRule;

  const currentTriggerEventOption = triggerEventOptions.find(
    (event) => event.value === triggerInfo.triggerEvent
  )!;

  const {
    triggerDomainSources,
    triggerCalendarSources,
    triggerGroupSources,
    triggerOrgSources,
    triggerUserSources,
    driveFolderTriggerSources,
    sharedDriveIds,
    driveRegExs,
  } = triggerInfo;

  const triggerCalendarSourcesList =
    triggerCalendarSources && triggerCalendarSources.length > 0
      ? triggerCalendarSources
      : selectedRule.triggerSources
      ? selectedRule.triggerSources
          .filter((a) => a.type === 'Calendar')
          .map((a) => a.value)
      : [];

  const triggerDomainSourcesList =
    triggerDomainSources.length > 0
      ? triggerDomainSources
      : selectedRule.triggerSources
      ? selectedRule.triggerSources
          ?.filter((a) => a.type === 'Domain')
          .map((a) => a.value)
      : [];
  const triggerGroupSourcesList =
    triggerGroupSources.length > 0
      ? triggerGroupSources
      : selectedRule.triggerSources
      ? selectedRule.triggerSources
          .filter((a) => a.type === 'Group')
          .map((a) => a.value)
      : [];
  const triggerOrgSourcesList =
    triggerOrgSources.length > 0
      ? triggerOrgSources
      : selectedRule.triggerSources
      ? selectedRule.triggerSources
          .filter((a) => a.type === 'Orgs')
          .map((a) => a.value)
      : [];
  const triggerUserSourcesList =
    triggerUserSources.length > 0
      ? triggerUserSources
      : selectedRule.triggerSources
      ? selectedRule.triggerSources
          .filter((a) => a.type === 'User')
          .map((a) => a.value)
      : [];
  const driveRegExsList =
    driveRegExs.length > 0
      ? driveRegExs
      : selectedRule.triggerSources
      ? selectedRule.triggerSources
          .filter((a) => a.type === 'RegEx')
          .map((a) => a.value)
      : [];

  const driveFolderTriggerSourcesList =
    driveFolderTriggerSources.length > 0
      ? driveFolderTriggerSources
      : selectedRule.triggerSources
      ? selectedRule.triggerSources.filter((a) => a.type === 'User Drive')
      : [];

  const sharedDriveIdsList =
    sharedDriveIds.length > 0
      ? sharedDriveIds
      : selectedRule.triggerSources
      ? selectedRule.triggerSources
          .filter((a) => a.type === 'Shared Drive')
          .map((a) => a.value)
      : [];

  const driveFolderIds = driveFolderTriggerSourcesList.map(
    (source) => source.driveFolderId
  );

  const { data: folderData } = useQueries({
    queries: driveFolderIds.map((id, index) => ({
      queryKey: [
        UserKeys.DriveFolder,
        id,
        driveFolderTriggerSourcesList[index].driveFolderOwner,
      ],
      queryFn: () =>
        getDocById(driveFolderTriggerSourcesList[index].driveFolderOwner!, id!),
      staleTime: 1000 * 60 * 5,
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        pending: results.some((result) => result.isPending),
      };
    },
  });

  const { data: sharedDrives } = useQuery({
    ...getSharedDrivesByIdsQuery(sharedDriveIds),
    enabled: sharedDriveIds.length > 0,
  });

  const { driveFileSizeFloor, driveFileSizeCeiling } = triggerInfo;
  function getFileSizeFilter() {
    let sizeComparison = '';
    let fileSizeType = '';
    let fileSizeFloor = driveFileSizeFloor;
    let fileSizeCeiling = driveFileSizeCeiling;

    if (selectedRule.sizeComparison) {
      sizeComparison = sizeComparisonOptions.find(
        (option) => option.value === selectedRule.sizeComparison
      )!.displayValue;
    } else {
      // Fallback logic
      sizeComparison =
        driveFileSizeFloor > 0 && driveFileSizeCeiling > 0
          ? 'Between'
          : driveFileSizeFloor > 0
          ? 'Greater Than'
          : 'Less Than';
    }

    if (selectedRule.sizeType) {
      fileSizeType = sizeTypeOptions.find(
        (option) => option.value === selectedRule.sizeType
      )!.displayValue;
    } else {
      fileSizeType = 'bytes';
      const floorInKB = driveFileSizeFloor / 1024;
      const ceilingInKB = driveFileSizeCeiling / 1024;
      const floorInMB = floorInKB / 1024;
      const ceilingInMB = ceilingInKB / 1024;

      if (
        (sizeComparison === 'Greater Than' && floorInMB >= 1) ||
        (sizeComparison === 'Less Than' && ceilingInMB >= 1) ||
        (sizeComparison === 'Between' && floorInMB >= 1)
      ) {
        fileSizeFloor = floorInMB;
        fileSizeCeiling = ceilingInMB;
        fileSizeType = 'MB';
      } else if (
        (sizeComparison === 'Greater Than' && floorInKB >= 1) ||
        (sizeComparison === 'Less Than' && ceilingInKB >= 1) ||
        (sizeComparison === 'Between' && floorInKB >= 1)
      ) {
        fileSizeFloor = floorInKB;
        fileSizeCeiling = ceilingInKB;
        fileSizeType = 'KB';
      }
    }

    switch (sizeComparison) {
      case 'Greater Than':
        return sizeComparison + ' ' + fileSizeFloor + ' ' + fileSizeType;

      case 'Less Than':
        return sizeComparison + ' ' + fileSizeCeiling + ' ' + fileSizeType;

      default:
        return (
          sizeComparison +
          ' ' +
          fileSizeFloor +
          ' and ' +
          fileSizeCeiling +
          ' ' +
          fileSizeType
        );
    }
  }

  const infoText = useMemo(() => {
    const triggerDescription = triggerEventOptions.find(
      (x) => x.value === triggerInfo.triggerEvent
    )?.displayInfoValue;

    let timeString = '';
    const occursEverytime = triggerInfo.occursEverytime;
    if (!occursEverytime) {
      const triggerThreshold = triggerInfo.triggerThreshold;
      timeString = ` ${triggerThreshold} or more times`;
      if (triggerInfo.hasTimeDuration) {
        const triggerDuration = triggerInfo.triggerDuration;
        const triggerDurationType = triggerInfo.triggerDurationType;
        timeString += ` within ${triggerDuration} ${triggerDurationType}`;
      }
    }

    let sourceString = '';

    if (currentTriggerEventOption.type === 'admin') {
      if (triggerOrgSourcesList.length === 1) {
        sourceString += ` within this organization unit (${triggerOrgSourcesList[0]})`;
      } else if (triggerOrgSourcesList.length > 1) {
        sourceString += ` within these organization units (${triggerOrgSourcesList
          .map((source) => source)
          .join(', ')})`;
      }

      if (
        triggerOrgSourcesList.length > 0 &&
        triggerDomainSourcesList.length > 0
      ) {
        sourceString += ` or`;
      }

      if (triggerDomainSourcesList.length === 1) {
        sourceString += ` within this domain (${triggerDomainSourcesList[0]})`;
      } else if (triggerDomainSourcesList.length > 1) {
        sourceString += ` within these domains (${triggerDomainSourcesList
          .map((source) => source)
          .join(', ')})`;
      }
    } else if (currentTriggerEventOption.type === 'drive') {
      if (triggerUserSourcesList.length === 1) {
        sourceString += ` by this user (${triggerUserSourcesList[0]})`;
      } else if (triggerUserSourcesList.length > 1) {
        sourceString += ` by these users (${triggerUserSourcesList
          .map((source) => source)
          .join(', ')})`;
      }

      if (
        triggerUserSourcesList.length > 0 &&
        triggerGroupSourcesList.length > 0
      ) {
        sourceString += ` or`;
      }

      if (triggerGroupSourcesList.length === 1) {
        sourceString += ` by users within this group (${triggerGroupSourcesList[0]})`;
      } else if (triggerGroupSourcesList.length > 1) {
        sourceString += ` by users within these groups (${triggerGroupSourcesList
          .map((source) => source)
          .join(', ')})`;
      }

      if (
        (triggerUserSourcesList.length > 0 ||
          triggerGroupSourcesList.length > 0) &&
        triggerOrgSourcesList.length > 0
      ) {
        sourceString += ` or`;
      }

      if (triggerOrgSourcesList.length === 1) {
        sourceString += ` by users within this organization unit (${triggerOrgSourcesList[0]})`;
      } else if (triggerOrgSourcesList.length > 1) {
        sourceString += ` by users within these organization units (${triggerOrgSourcesList
          .map((source) => source)
          .join(', ')})`;
      }

      if (
        (triggerUserSourcesList.length > 0 ||
          triggerGroupSourcesList.length > 0 ||
          triggerOrgSourcesList.length > 0) &&
        triggerDomainSourcesList.length > 0
      ) {
        sourceString += ` or`;
      }

      if (triggerDomainSourcesList.length === 1) {
        sourceString += ` by users within this domain (${triggerDomainSourcesList[0]})`;
      } else if (triggerDomainSourcesList.length > 1) {
        sourceString += ` by users within these domains (${triggerDomainSourcesList
          .map((source) => source)
          .join(', ')})`;
      }

      if (
        (triggerUserSourcesList.length > 0 ||
          triggerGroupSourcesList.length > 0 ||
          triggerOrgSourcesList.length > 0 ||
          triggerDomainSourcesList.length > 0) &&
        sharedDriveIdsList.length > 0
      ) {
        sourceString += ` or`;
      }

      if (sharedDriveIdsList.length === 1) {
        const driveName =
          sharedDrives?.find((drive) => drive.id === sharedDriveIdsList[0])
            ?.name || sharedDriveIdsList[0];
        sourceString += ` within this shared drive (${driveName})`;
      } else if (sharedDriveIdsList.length > 1) {
        const driveNames = sharedDriveIdsList.map((id) => {
          const drive = sharedDrives?.find((drive) => drive.id === id);
          return drive?.name || id;
        });
        sourceString += ` within these shared drives (${driveNames.join(
          ', '
        )})`;
      }

      if (
        (triggerUserSourcesList.length > 0 ||
          triggerGroupSourcesList.length > 0 ||
          triggerOrgSourcesList.length > 0 ||
          triggerDomainSourcesList.length > 0 ||
          sharedDriveIdsList.length > 0) &&
        driveFolderTriggerSourcesList.length > 0
      ) {
        sourceString += ` or`;
      }

      if (driveFolderTriggerSourcesList.length === 1) {
        sourceString += ` within this user's folder (${
          driveFolderTriggerSourcesList[0].driveFolderOwner
        }: ${folderData && folderData.length > 0 ? folderData[0]?.title : ''})`;
      } else if (driveFolderTriggerSourcesList.length > 1) {
        sourceString += ` within these user's folders (${folderData
          ?.map(
            (source, index) =>
              `${driveFolderTriggerSourcesList[index].driveFolderOwner}: ${source?.title}`
          )
          .join(', ')})`;
      }

      if (
        (triggerUserSourcesList.length > 0 ||
          triggerGroupSourcesList.length > 0 ||
          triggerOrgSourcesList.length > 0 ||
          triggerDomainSourcesList.length > 0 ||
          sharedDriveIdsList.length > 0 ||
          driveFolderTriggerSourcesList.length > 0) &&
        driveRegExsList.length > 0
      ) {
        sourceString += ` or`;
      }

      if (driveRegExsList.length === 1) {
        sourceString += ` with a title that matches this regular expression (${driveRegExsList[0]})`;
      } else if (driveRegExsList.length > 1) {
        sourceString += ` with a title that matches these regular expressions (${driveRegExsList
          .map((source) => source)
          .join(', ')})`;
      }
    } else if (currentTriggerEventOption.type === 'calendar') {
      if (!selectedRule.allSource) {
        const sourceList =
          triggerCalendarSourcesList?.map(
            (source) => calendarEmailToNameMap.get(source) ?? source
          ) ?? [];
        sourceString += ` within these calendar resources: ${sourceList.join(
          ', '
        )}`;
      } else {
        sourceString += ` within all calendar resources`;
      }
    } else {
      sourceString = ` from an actor within these entities: `;
    }

    // DRIVE FILTERS
    const hasFileTypeFilter = triggerInfo.fileMimeTypes.length > 0;
    const hasFileSizeFilter =
      triggerInfo.driveFileSizeCeiling > 0 ||
      triggerInfo.driveFileSizeFloor > 0;

    const {
      fileIsSharedToDomain,
      fileIsSharedPublic,
      fileSharedToUserExternalToDomain,
      canFindInSearchResults,
      fileSharedToUserInternalToDomain,
      mustHaveLinkToAccess,
      fileSharedToSpecificUser,
    } = triggerInfo;

    const hasSharingPermissionsFilter =
      fileIsSharedPublic ||
      fileIsSharedToDomain ||
      fileSharedToUserExternalToDomain ||
      fileSharedToUserInternalToDomain ||
      fileSharedToSpecificUser.length > 0 ||
      selectedRule.hasSharingPermissionsFilter ||
      !!selectedRule.selectedSharingOption ||
      !!selectedRule.hasSelectedDomainSharing;

    const hasSelectedDomainSharing =
      fileIsSharedToDomain && (canFindInSearchResults || mustHaveLinkToAccess);

    if (hasFileTypeFilter) {
      const fileTypes = triggerInfo.fileMimeTypes;
      const displayNames = fileTypes.map((fileType) => {
        const option = documentTypeOptions.find((x) => x.value === fileType);
        return option?.displayValue;
      });

      if (displayNames.length === 1) {
        sourceString += ` with a file type of ${displayNames[0]}`;
      } else {
        sourceString += ` with the following file types (${displayNames.join(
          ', '
        )})`;
      }
    }

    if (hasFileTypeFilter && hasFileSizeFilter) {
      sourceString += ' or';
    }

    if (hasFileSizeFilter) {
      const sizeComparisons = getFileSizeFilter().toLowerCase();
      sourceString += ` with a file size ${sizeComparisons}`;
    }

    if (
      (hasFileTypeFilter || hasFileSizeFilter) &&
      hasSharingPermissionsFilter
    ) {
      sourceString += ' or';
    }
    if (
      selectedRule.triggerInfo.triggerGroup === 'admin' &&
      (selectedRule.triggerInfo.triggerEvent === 'CREATE_USER' ||
        selectedRule.triggerInfo.triggerEvent === 'ROLE_PERMISSION_CHANGE' ||
        selectedRule.triggerInfo.triggerEvent === 'UPDATE' ||
        selectedRule.triggerInfo.triggerEvent === 'ROLE_MEMBER_UPDATE')
    ) {
      const strProfileFieldType = profileFieldFilter?.profileFieldType
        ? resolveProfileFieldDisplay(profileFieldFilter.profileFieldType)
        : '';

      if (strProfileFieldType) {
        const valDisplay =
          profileFieldFilter?.profileFieldValues
            ?.filter((v) => Boolean(v.value))
            .map((v) => v.value)
            .join(', ') ||
          profileFieldFilter?.profileFieldValue ||
          '';
        sourceString += ` with a ${strProfileFieldType} matching ${valDisplay}`;
      }
    }
    if (hasSharingPermissionsFilter) {
      if (selectedRule.selectedSharingOption) {
        switch (selectedRule.selectedSharingOption) {
          case 'fileIsSharedPublic':
            sourceString += ` that is shared publicly`;
            break;
          case 'fileSharedToUserExternalToDomain':
            sourceString += ` that is shared with a user outside of the domain`;
            break;
          case 'fileSharedToUserInternalToDomain':
            sourceString += ` that is shared with a user within the domain`;
            break;
          case 'fileSharedToSpecificUser': {
            if (fileSharedToSpecificUser.length === 1) {
              sourceString += ` that is shared with ${fileSharedToSpecificUser[0]}`;
            } else {
              sourceString += ` that is shared with these users (${fileSharedToSpecificUser.join(
                ', '
              )})`;
            }
            break;
          }
          case 'fileIsSharedToDomain': {
            if (selectedRule.hasSelectedDomainSharing) {
              const canFindInSearchResults = triggerInfo.canFindInSearchResults;
              const mustHaveLinkToAccess = triggerInfo.mustHaveLinkToAccess;

              if (canFindInSearchResults) {
                sourceString += ` that is shared with the domain and can be found in search results`;
              }

              if (mustHaveLinkToAccess) {
                sourceString += ` that is shared with the domain and requires a link to access`;
              }
            } else {
              sourceString += ` that is shared with the domain`;
            }
            break;
          }
        }
      }
      if (fileIsSharedPublic) {
        sourceString += ` that is shared publicly`;
      } else if (fileSharedToUserExternalToDomain) {
        sourceString += ` that is shared with a user outside of the domain`;
      } else if (fileSharedToUserInternalToDomain) {
        sourceString += ` that is shared with a user within the domain`;
      } else if (fileSharedToSpecificUser.length > 0) {
        if (fileSharedToSpecificUser.length === 1) {
          sourceString += ` that is shared with ${fileSharedToSpecificUser[0]}`;
        } else {
          sourceString += ` that is shared with these users (${fileSharedToSpecificUser.join(
            ', '
          )})`;
        }
      } else if (fileIsSharedToDomain) {
        if (hasSelectedDomainSharing) {
          const canFindInSearchResults = triggerInfo.canFindInSearchResults;
          const mustHaveLinkToAccess = triggerInfo.mustHaveLinkToAccess;

          if (canFindInSearchResults) {
            sourceString += ` that is shared with the domain and can be found in search results`;
          }

          if (mustHaveLinkToAccess) {
            sourceString += ` that is shared with the domain and requires a link to access`;
          }
        } else {
          sourceString += ` that is shared with the domain`;
        }
      }
    }

    const groupEventValues = [
      'ADD_GROUP_MEMBER',
      'REMOVE_GROUP_MEMBER',
      'UPDATE_GROUP_MEMBER',
    ];
    const groupFilter = selectedRule.groupFilter;
    if (groupEventValues.includes(triggerInfo.triggerEvent) && groupFilter) {
      const groupFilterParts: string[] = [];

      if (groupFilter.hasGroupNameFilter) {
        if (groupFilter.groupNameMode === 'regex') {
          if (groupFilter.groupNameRegex) {
            groupFilterParts.push(
              `the group name matches the regular expression "${groupFilter.groupNameRegex}"`
            );
          }
        } else if (groupFilter.groupName) {
          groupFilterParts.push(
            groupFilter.groupNameExact
              ? `the group name is "${groupFilter.groupName}"`
              : `the group name contains "${groupFilter.groupName}"`
          );
        }
      }

      if (groupFilter.hasMemberCountFilter) {
        const isRange =
          groupFilter.memberCountMode === 'range' &&
          triggerInfo.triggerEvent === 'UPDATE_GROUP_MEMBER';
        if (isRange) {
          groupFilterParts.push(
            `the group has fewer than ${
              groupFilter.memberCountRangeLessThan ?? 0
            } or more than ${
              groupFilter.memberCountRangeGreaterThan ?? 0
            } members`
          );
        } else {
          const memberCountPhrases: Record<string, string> = {
            equals: 'exactly',
            greaterThan: 'more than',
            lessThan: 'fewer than',
            greaterThanOrEqual: 'at least',
            lessThanOrEqual: 'at most',
          };
          const phrase =
            memberCountPhrases[groupFilter.memberCountOperator ?? 'equals'] ??
            'exactly';
          groupFilterParts.push(
            `the group has ${phrase} ${groupFilter.memberCount ?? 0} members`
          );
        }
      }

      if (groupFilterParts.length > 0) {
        sourceString += ` where ${groupFilterParts.join(' and ')}`;
      }
    }

    let actionString = '...';
    const actionTodo = actionInfo.actionTodo;
    if (actionTodo && actionTodo.length > 0) {
      actionString = `, the following actions will be taken: `;
      actionString += actionTodo
        .map((action) => {
          const actionOption = actionOptions.find((x) => x.value === action);
          return actionOption?.displayValue;
        })
        .join(', ');
    }

    return `When ${triggerDescription}${timeString}${sourceString}${actionString}`;
  }, [
    triggerInfo,
    currentTriggerEventOption,
    triggerOrgSourcesList,
    triggerDomainSourcesList,
    triggerUserSourcesList,
    triggerGroupSourcesList,
    sharedDriveIdsList,
    driveFolderTriggerSourcesList,
    driveRegExsList,
    selectedRule,
    sharedDrives,
    folderData,
    calendarEmailToNameMap,
    profileFieldFilter,
    resolveProfileFieldDisplay,
    actionInfo,
  ]);

  const resolvedRuleAsModel: RuleModel = {
    ...ruleAsModel,
    triggerInfo: {
      ...ruleAsModel.triggerInfo,
      triggerUserSources: triggerUserSourcesList,
      triggerGroupSources: triggerGroupSourcesList,
      triggerOrgSources: triggerOrgSourcesList,
      triggerDomainSources: triggerDomainSourcesList,
      triggerCalendarSources: triggerCalendarSourcesList,
      sharedDriveIds: sharedDriveIdsList,
      driveRegExs: driveRegExsList,
      driveFolderTriggerSources: driveFolderTriggerSourcesList.map(
        (source) => ({
          driveFolderId: source.driveFolderId ?? '',
          driveFolderOwner: source.driveFolderOwner ?? '',
        })
      ),
    },
  };

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {selectedRule.basicInfo.ruleName || 'N/A'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedRule.basicInfo.description}
        </Typography>
      </Stack>
      <RulesDetailsSummaryOld
        selectedRule={resolvedRuleAsModel}
        allPolicies={allPolicies}
        reviewStepText={infoText}
      />
    </Stack>
  );
}
